<template>
  <div class="l-main">
    <div class="l-main__header">
      <ul class="c-breadcrumbs">
        <li class="c-breadcrumbs__item">
          <a class="c-breadcrumbs__link c-breadcrumbs__home c-breadcrumbs__home--disabled">
            <md-icon>home</md-icon>
            <span>Home</span>
          </a>
        </li>
        <li class="c-breadcrumbs__item--separator">
          /
        </li>
        <li class="c-breadcrumbs__item" @click="goToWebsites">
          <a class="c-breadcrumbs__link c-breadcrumbs__home">Websites</a>
        </li>
        <li class="c-breadcrumbs__item--separator">
          /
        </li>
        <li class="c-breadcrumbs__item is-current">
          <a class="c-breadcrumbs__link c-breadcrumbs__home">{{ websiteName }}</a>
        </li>
      </ul>
    </div>
    <div class="l-main__ctn u-box c-website">
      <aside
        class="c-website__nav"
        aria-label="Website sections"
      >
        <div class="ui-field c-website__filter">
          <div class="ui-field__control ui-field__control--with-action">
            <app-icon
              class="ui-field__icon"
              name="search"
              :size="18"
            />
            <input
              v-model="sectionFilter"
              class="ui-field__input"
              type="text"
              placeholder="Find section…"
              aria-label="Find section"
            >
            <button
              v-if="sectionFilter"
              type="button"
              class="ui-field__action"
              title="Clear"
              aria-label="Clear"
              @click="sectionFilter = ''"
            >
              <app-icon
                name="close"
                :size="18"
              />
            </button>
          </div>
        </div>

        <nav class="c-website__nav-list">
          <template v-for="group in navigationGroups">
            <p
              :key="`${group.key}-label`"
              class="c-website__nav-label"
            >
              {{ group.label }}
            </p>
            <button
              v-for="tab in group.tabs"
              :key="`${$route.params.id}-${tab.tab}`"
              type="button"
              class="c-website__nav-item"
              :class="{ 'is-active': selectedTab && tab.tab === selectedTab.tab }"
              :aria-current="selectedTab && tab.tab === selectedTab.tab ? 'page' : null"
              @click="changeActiveTab(tab)"
            >
              <app-icon
                :name="tab.icon"
                :size="18"
              />
              <span>{{ tab.label }}</span>
            </button>
          </template>

          <p
            v-if="!navigationGroups.length"
            class="c-website__nav-empty"
          >
            No sections match “{{ sectionFilter }}”.
          </p>
        </nav>
      </aside>

      <section class="c-website__panel">
        <header
          v-if="selectedTab && selectedTab.label"
          class="c-website__panel-header"
        >
          <span class="c-website__panel-icon">
            <app-icon
              :name="selectedTab.icon"
              :size="20"
            />
          </span>
          <h2 class="c-website__panel-title">
            {{ selectedTab.label }}
          </h2>
        </header>

        <div class="c-website__panel-body">
          <template v-if="website && selectedTab && selectedTab.tab">
            <component
              :is="selectedTab.tab"
              v-if="!selectedTab.id"
              :website="website"
              class="c-website__component"
            />
            <generic-online-configuration
              v-else
              :website="website"
              :section="selectedTab"
            />
          </template>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { websiteTabs } from '../../../../const';
import { websiteService } from "../../../../services/websites";
import SeoRules from '../../seo-rules';
import Tooltips from '../../tooltips';
import CustomScripts from '../../custom-scripts';
import BotScripts from '../../bot-scripts';
import MetaTags from '../../meta-tags';
import GenericOnlineConfiguration from '../../generic-online-configuration';
import CustomPages from '../../configurations/components/custom-pages'
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import {PERMISSION_NAMES} from "@/const/online-permissions";
import AppIcon from "@/shared/app-icon/AppIcon";

const WEBSITE_TAB_ICONS = {
  'seo-rules': 'travel_explore',
  tooltips: 'info',
  'custom-scripts': 'code',
  'bot-scripts': 'smart_toy',
  'meta-tags': 'sell',
};

// configuration sections come from data; pick an icon by keyword, fall back to a generic one
const SECTION_ICONS = [
  ['banner', 'view_carousel'], ['footer', 'bottom_navigation'], ['landing', 'web'], ['stream', 'live_tv'],
  ['account', 'person'], ['social', 'share'], ['jackpot', 'savings'], ['tag', 'sell'], ['chat', 'chat'],
  ['registration', 'how_to_reg'], ['ticket', 'confirmation_number'], ['casino', 'casino'],
  ['geo', 'location_on'], ['location', 'location_on'], ['login', 'login'], ['help', 'help'],
  ['meta', 'sell'], ['virtual', 'sports_esports'], ['general', 'tune'], ['question', 'quiz'],
  ['exclusion', 'block'], ['simulation', 'science'], ['script', 'code'],
];

export default {
  name: "SingleWebsite",

  components: {
    CustomPages,
    SeoRules,
    Tooltips,
    CustomScripts,
    GenericOnlineConfiguration,
    BotScripts,
    MetaTags,
    AppIcon,
  },

  mixins: [onlinePermissionsMixin],

  data() {
    return {
      website: null,
      selectedTab: {
        tab: '',
      },
      sectionFilter: '',
    };
  },

  computed: {
    ...mapGetters('account', ['user']),
    ...mapGetters('onlineConfiguration', ['configurations', 'sections', 'subsections', 'types']),

    filteredWebsiteTabs() {
      const configurationTabs = this.canRead(`${PERMISSION_NAMES.WEB_SITES.key}.${PERMISSION_NAMES.WEBSITES_CONFIGURATION.key}`)
        ? this.getSectionsFromConfigurations()
        : [];

      return websiteTabs.filter(websiteTab => {
        if (websiteTab.onlyAdmins) {
          return this.isAdmin();
        }
        return this.canRead(websiteTab.permission);
      })
        .map(websiteTab => ({ ...websiteTab, icon: WEBSITE_TAB_ICONS[websiteTab.tab] || 'tune' }))
        .concat(configurationTabs);
    },

    navigationGroups() {
      const filter = this.sectionFilter.trim().toLowerCase();
      const matches = tab => !filter || tab.label.toLowerCase().includes(filter);

      return [
        { key: 'website', label: 'Website', tabs: this.filteredWebsiteTabs.filter(tab => !tab.id && matches(tab)) },
        { key: 'configuration', label: 'Configuration', tabs: this.filteredWebsiteTabs.filter(tab => tab.id && matches(tab)) },
      ].filter(group => group.tabs.length);
    },

    websiteName() {
      return this.website && this.website.name
    }
  },

  async created() {
    await this.getWebsite();
    await Promise.all([
      this.getOnlineConfigurationSections(),
      this.getOnlineConfigurationSubsections(),
      this.getOnlineConfigurationTypes(),
      this.getWebsiteConfiguration(this.website.id)
    ])

    this.setInitialTab()
  },

  methods: {
    ...mapActions('onlineConfiguration', [
      'getWebsiteConfiguration',
      'getOnlineConfigurationSections',
      'getOnlineConfigurationSubsections',
      'getOnlineConfigurationTypes'
    ]),

    goToWebsites() {
      this.$router.push({ name: "websites" });
    },

    async getWebsite() {
      const website = await websiteService.getSingleWebsite(this.$route.params.id)

      this.website = website.data
    },

    getSectionsFromConfigurations() {
      const sectionIds = this.configurations.map(configuration => configuration.sectionId)
      const uniqueIds = [...new Set(sectionIds)]

      return uniqueIds.map(id => {
        const section = this.sections.find(section => section.id === id);

        const label = section.name.replace('-', ' ');

        return {
          id: section.id,
          tab: section.name,
          label: label.charAt(0).toUpperCase() + label.slice(1),
          icon: this.getSectionIcon(section.name),
        }
      })
    },

    getSectionIcon(name) {
      const lowerName = String(name).toLowerCase();
      const match = SECTION_ICONS.find(([keyword]) => lowerName.includes(keyword));

      return match ? match[1] : 'settings';
    },

    setInitialTab() {
      this.selectedTab = this.filteredWebsiteTabs[0]
    },

    changeActiveTab(tab) {
      this.selectedTab = tab;
    },
  },
};
</script>

<style lang="scss" scoped>
.c-website {
  flex-direction: row;
  overflow: hidden;
}

// ---------- section navigator ----------
.c-website__nav {
  display: flex;
  flex-direction: column;
  flex: 0 0 248px;
  min-height: 0;
  border-right: 1px solid var(--border-default);
  background-color: var(--bg-subtle);
}

.c-website__filter {
  padding: 16px 16px 8px;

  .ui-field__input {
    height: 36px;
    font-size: 13px;
  }

  .ui-field__icon {
    left: 10px;
  }

  .ui-field__icon + .ui-field__input {
    padding-left: 34px;
  }

  .ui-field__action {
    width: 30px;
    height: 30px;
  }
}

.c-website__nav-list {
  flex: 1;
  min-height: 0;
  padding: 4px 12px 16px;
  overflow-y: auto;
  scrollbar-width: thin;
}

.c-website__nav-label {
  margin: 16px 0 6px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);

  &:first-child {
    margin-top: 4px;
  }
}

.c-website__nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--text-secondary);
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .c-app-icon {
    color: var(--text-muted);
  }

  &:hover {
    background-color: var(--bg-hover);
    color: var(--text-primary);
  }

  &:focus-visible {
    box-shadow: var(--focus-ring);
  }

  &.is-active {
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-sm);
    color: var(--text-primary);
    font-weight: 600;

    .c-app-icon {
      color: var(--brand-600);
    }
  }
}

.c-website__nav-empty {
  margin: 12px 8px;
  font-size: 13px;
  color: var(--text-muted);
}

// ---------- content ----------
.c-website__panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

.c-website__panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-default);
}

.c-website__panel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  background-color: var(--brand-50);
  color: var(--brand-600);
}

.c-website__panel-title {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--text-primary);
}

.c-website__panel-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 20px 24px 24px;
  overflow-y: auto;
  scrollbar-gutter: stable;

  // sections still wrap themselves in page layouts; flatten them so this is the only scroll area
  ::v-deep .l-main,
  ::v-deep .l-main__ctn,
  ::v-deep .c-website__component {
    flex: 1 1 auto;
    min-width: 0;
    height: auto;
    min-height: auto;
    max-height: none;
    overflow: visible;
  }

  // sticky "Submit" bar: a full-width footer flush with the panel's bottom edge.
  // Sticky stops at the scroll container's padding, so without this the last 24px of content
  // scrolled visibly underneath the bar.
  ::v-deep .u-sticky-submit-wrapper {
    bottom: -24px;
    margin: 24px -24px -24px !important;
    padding: 16px 24px 20px;
  }
}
</style>
